/// <amd-module name="TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Components/SSHPublicKeysTableView" />
import React = require("react");

import { VssComponent } from "VSS/Platform/Layout";
import { ITableColumn, ITableRow, SimpleTableCell, Table, TableColumnStyle, ColumnMore } from "VSSUI/Table";
import { Card } from "VSSUI/Card";
import { Tooltip } from "VSSUI/TooltipEx";
import { Icon } from 'VSSUI/Icon';
import { localeFormat } from "VSS/Core/Util/String";
import { IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ListSelection } from "VSSUI/List";
import { filterXSS } from "xss";
import { SSHToken } from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Contracts";
import * as Resources from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Resources";

interface ISSHPublicKeysTableViewProps {
    tokenData: ObservableArray<SSHToken>;
    onDeleteKey: (selectedKey: SSHToken) => void;
    onShowKeyDetails: (selectedKey: SSHToken) => void;
}

export default class SSHPublicKeysTableViewComponent extends VssComponent<ISSHPublicKeysTableViewProps> {
    private _selection = new ListSelection(false);

    public render() {
        return (
            <div>
                <Card className="ssh-public-keys-table-card-content" contentProps={{ contentPadding: false }}>
                    <Table
                        itemProvider={this.props.tokenData}
                        columns={this._getColumnItems()}
                        onActivate={(ev: any, tableRow: ITableRow<SSHToken>) => {
                            this.props.onShowKeyDetails(tableRow.data)
                        }}
                        className="ssh-public-keys-list"
                        selection={this._selection}
                        singleClickActivation={true}
                    />
                </Card>
            </div>
        );
    }

    private _getColumnItems(): ITableColumn<SSHToken>[] {
        return [
            {
                id: "name",
                name: Resources.Name,
                renderCell: this._renderDisplayNameColumn,
                width: new ObservableValue(-31),
                columnStyle: TableColumnStyle.Primary,
                onSize: this._onSizeColumn
            },
            {
                id: "fingerprint",
                name: Resources.Fingerprint,
                renderCell: this._renderFingerprintColumn,
                width: new ObservableValue(-35),
                columnStyle: TableColumnStyle.Secondary,
                onSize: this._onSizeColumn
            },
            {
                id: "expiration-date",
                name: Resources.DateValidUntil,
                renderCell: (row, colum, tableColum, item) => 
                    this._renderDateColum(row, colum, tableColum, item.token.validTo),
                width: new ObservableValue(-17),
                columnStyle: TableColumnStyle.Secondary,
                onSize: this._onSizeColumn
            },
            {
                id: "date-added",
                name: Resources.DateAdded,
                renderCell: (row, colum, tableColum, item) =>
                    this._renderDateColum(row, colum, tableColum, item.token.validFrom),
                width: new ObservableValue(-17),
                columnStyle: TableColumnStyle.Secondary,
                onSize: this._onSizeColumn
            },
            {
                id: "status",
                name: Resources.Status,
                renderCell: this._renderStatusColumn,
                width: new ObservableValue(-17),
                columnStyle: TableColumnStyle.Secondary,
                onSize: this._onSizeColumn
            },
            new ColumnMore<SSHToken>((item: SSHToken) => (
                {
                    id: "sub-menu",
                    items: [
                        {
                            id: Resources.RemoveSSHPublicKey,
                            iconProps: {
                                iconName: Resources.Delete
                            },
                            text: Resources.RemoveSSHPublicKey,
                            disabled: !item.token.isValid,
                            onActivate: () => this.props.onDeleteKey(item)
                        }
                    ]
                }
            ), (item => item.token.isValid))
        ];
    }

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<SSHToken>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private _renderDisplayNameColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<SSHToken>,
        tableItem: SSHToken
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="display-ssh-description-details">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">{filterXSS(tableItem.token.displayName)}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderFingerprintColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<SSHToken>,
        tableItem: SSHToken
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="display-ssh-fingerprint-details">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">{tableItem.fingerprint}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }
    
    private _renderDateColum(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<SSHToken>,
        date: Date
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="display-ssh-date-added-details">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">{localeFormat("{0}", date)}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderStatusColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<SSHToken>,
        tableItem: SSHToken
    ): JSX.Element {
        const { token } = tableItem;
        const currentDate = new Date();
        const status = !token.isValid ? Resources.Revoked
                     : token.validTo < currentDate ? Resources.Expired : Resources.Active;
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="display-ssh-status-details">
                <Icon className={`status-icon ${status.toLowerCase()}`} iconName="LocationDot" title={status} />
                {status}
            </SimpleTableCell>
        );
    }
}
